import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, MapPin, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, TIPI_PROBLEMA, AZIENDA, IMPIANTI, URGENZE } from "@/lib/config";
import { traccia } from "@/lib/tracking";

const testoWhatsApp = (r) => [
  `Nuova richiesta di intervento (${URGENZE[r.urgenza]?.label})`,
  `Problema: ${r.tipo_problema} - impianto ${IMPIANTI[r.impianto]}`,
  `Descrizione: ${r.descrizione}`,
  `Nome: ${r.nome}`,
  `Telefono: ${r.telefono}`,
  r.email && `Email: ${r.email}`,
  `Indirizzo: ${r.indirizzo}`,
].filter(Boolean).join("\n");

const VUOTO = {
  tipo_problema: "", impianto: "civile", urgenza: "", descrizione: "", nome: "",
  telefono: "", email: "", indirizzo: "", canale_preferito: "telefono",
};

const Scelta = ({ value, current, onClick, children, testid }) => (
  <button type="button" onClick={() => onClick(value)} data-testid={testid}
    className={`rounded-xl border px-4 py-3 text-sm font-semibold text-left transition-colors ${current === value ? "border-sky-600 bg-sky-50 text-sky-800 ring-2 ring-sky-200" : "border-slate-200 bg-white hover:border-slate-400"}`}>
    {children}
  </button>
);

const Campo = ({ label, children }) => (
  <div className="space-y-2"><Label className="font-semibold">{label}</Label>{children}</div>
);

export const RichiestaForm = ({ prefill }) => {
  const [f, setF] = useState(VUOTO);
  const [loading, setLoading] = useState(false);
  const [inviato, setInviato] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (prefill) { setInviato(null); const { t, ...campi } = prefill; setF((p) => ({ ...p, ...campi })); }
  }, [prefill]);

  const invia = async (e) => {
    e.preventDefault();
    if (!f.tipo_problema || !f.urgenza) return toast.error("Seleziona il tipo di problema e l'urgenza");
    if (!privacy) return toast.error("Per inviare la richiesta devi accettare l'informativa privacy");
    setLoading(true);
    try {
      await axios.post(`${API}/interventi`, f);
      setInviato(f);
      traccia("richiesta_inviata", { tipo_problema: f.tipo_problema, urgenza: f.urgenza, impianto: f.impianto });
      setF(VUOTO);
      setPrivacy(false);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
    } catch (err) {
      toast.error("Controlla i dati inseriti (descrizione min. 5 caratteri, telefono valido).");
    } finally { setLoading(false); }
  };

  return (
    <section id="richiesta" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-10" data-testid="richiesta-section">
      <div className="lg:col-span-4">
        <p className="text-xs font-bold uppercase tracking-wider text-sky-700">Richiesta intervento</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">Descrivi il problema, ti richiamiamo noi.</h2>
        <p className="mt-4 text-slate-600">Bastano pochi dati. Per un'emergenza (allagamento, tubo esploso) chiama subito il <a href={AZIENDA.telefonoLink} className="font-bold text-orange-600 underline" data-testid="form-call-link">{AZIENDA.telefono}</a>.</p>
        <a href={AZIENDA.mappaLink} target="_blank" rel="noreferrer" className="mt-6 flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm hover:border-sky-400 transition-colors" data-testid="form-indirizzo-sede-link">
          <MapPin className="w-5 h-5 text-sky-600 shrink-0" />
          <span><span className="block font-bold">La nostra sede</span>{AZIENDA.indirizzo}</span>
        </a>
      </div>
      <div className="lg:col-span-8">
        {inviato ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="richiesta-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h3 className="mt-4 text-xl font-bold">Richiesta ricevuta</h3>
            <p className="mt-2 text-slate-600">Un tecnico ti contatterà sul canale che hai scelto. Per una risposta ancora più rapida, inviaci i dettagli anche su WhatsApp.</p>
            <a href={`${AZIENDA.whatsappLink}?text=${encodeURIComponent(testoWhatsApp(inviato))}`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-green-600 text-white px-6 h-12 font-bold hover:bg-green-700 transition-colors" data-testid="richiesta-whatsapp-btn">
              <MessageCircle className="w-5 h-5" /> Invia la richiesta su WhatsApp
            </a>
            <br />
            <button onClick={() => setInviato(null)} className="mt-6 rounded-full bg-sky-600 text-white px-6 py-3 font-bold hover:bg-sky-700 transition-colors" data-testid="richiesta-new-btn">Invia un'altra richiesta</button>
          </div>
        ) : (
          <form onSubmit={invia} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8 shadow-sm space-y-6" data-testid="richiesta-form">
            <Campo label="1. Che problema hai?">
              <Select value={f.tipo_problema} onValueChange={set("tipo_problema")}>
                <SelectTrigger className="h-12" data-testid="form-tipo-select"><SelectValue placeholder="Seleziona il tipo di problema" /></SelectTrigger>
                <SelectContent>{TIPI_PROBLEMA.map((t) => <SelectItem key={t} value={t} data-testid={`form-tipo-option-${TIPI_PROBLEMA.indexOf(t)}`}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Campo>
            <Campo label="2. Tipo di impianto">
              <div className="grid grid-cols-3 gap-2">
                <Scelta value="civile" current={f.impianto} onClick={set("impianto")} testid="form-impianto-civile">Civile</Scelta>
                <Scelta value="industriale" current={f.impianto} onClick={set("impianto")} testid="form-impianto-industriale">Industriale</Scelta>
                <Scelta value="speciale" current={f.impianto} onClick={set("impianto")} testid="form-impianto-speciale">Speciale</Scelta>
              </div>
            </Campo>
            <Campo label="3. Quanto è urgente?">
              <div className="grid sm:grid-cols-3 gap-2">
                <Scelta value="emergenza" current={f.urgenza} onClick={set("urgenza")} testid="form-urgenza-emergenza"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-2" />Emergenza</Scelta>
                <Scelta value="oggi" current={f.urgenza} onClick={set("urgenza")} testid="form-urgenza-oggi"><span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500 mr-2" />In giornata</Scelta>
                <Scelta value="programmato" current={f.urgenza} onClick={set("urgenza")} testid="form-urgenza-programmato"><span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-400 mr-2" />Programmato</Scelta>
              </div>
            </Campo>
            <Campo label="4. Descrivi il problema">
              <Textarea required minLength={5} rows={4} value={f.descrizione} onChange={set("descrizione")} placeholder="Es. perdita sotto il lavello della cucina, l'acqua gocciola dal tubo..." data-testid="form-descrizione-input" />
            </Campo>
            <div className="grid sm:grid-cols-2 gap-4">
              <Campo label="Nome e cognome"><Input required value={f.nome} onChange={set("nome")} className="h-12" data-testid="form-nome-input" /></Campo>
              <Campo label="Telefono"><Input required type="tel" value={f.telefono} onChange={set("telefono")} className="h-12" data-testid="form-telefono-input" /></Campo>
              <Campo label="Email (facoltativa)"><Input type="email" value={f.email} onChange={set("email")} className="h-12" data-testid="form-email-input" /></Campo>
              <Campo label="Indirizzo intervento"><Input required value={f.indirizzo} onChange={set("indirizzo")} placeholder="Via, civico, città" className="h-12" data-testid="form-indirizzo-input" /></Campo>
            </div>
            <Campo label="Come preferisci essere contattato?">
              <div className="grid grid-cols-3 gap-2">
                <Scelta value="telefono" current={f.canale_preferito} onClick={set("canale_preferito")} testid="form-canale-telefono">Chiamata</Scelta>
                <Scelta value="whatsapp" current={f.canale_preferito} onClick={set("canale_preferito")} testid="form-canale-whatsapp">WhatsApp</Scelta>
                <Scelta value="email" current={f.canale_preferito} onClick={set("canale_preferito")} testid="form-canale-email">Email</Scelta>
              </div>
            </Campo>
            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer" data-testid="form-privacy-label">
              <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 w-5 h-5 accent-sky-600 shrink-0" data-testid="form-privacy-checkbox" />
              <span>Ho letto l'<Link to="/privacy" className="font-semibold text-sky-700 underline" data-testid="form-privacy-link">informativa privacy</Link> e acconsento al trattamento dei miei dati personali per la gestione della richiesta.</span>
            </label>
            <button disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 text-white px-8 h-12 font-bold hover:bg-sky-700 disabled:opacity-60 transition-colors" data-testid="form-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Invia richiesta
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
